import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Checkbox, Form } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { ButtonIwText } from '@/_pages/interwill/components/ButtonIwText';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { SelectAssetOwnershipType } from '@/entities/assets';
import { useIwEditingStore } from '@/entities/interwills';
import { media } from '@/shared/components';
import { Alert } from '@/shared/components/Alert';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { OwnershipType } from '@/shared/lib/sj-orm/models/asset.dto';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { SelectAssetsModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

type SelectAssetsModalParams = {
  selectedAssets: DtoId[];
  assetOwnerships: Record<DtoId, OwnershipType>;
};

export const SelectAssetsModal: FC<SelectAssetsModalProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'selectAssets' });

  const modals = useModalsStore();
  const { assets: savedAssets, updateAssets } = useIwEditingStore();
  const {
    control,
    handleSubmit: formHandleSubmit,
    formState,
    watch,
  } = useForm<SelectAssetsModalParams>({
    defaultValues: {
      selectedAssets: [],
      assetOwnerships: {},
    },
  });

  const assetOwnerships = watch('assetOwnerships');
  const selectedAssetIds = watch('selectedAssets');

  const [tab, setTab] = useState(0);

  const sjDatabase = useSJDatabase();
  const assets = sjDatabase.collections.assets?.findMany(
    (item) =>
      !savedAssets.find((n) => n.id === item.id) &&
      (!item.ownerShipType || item.ownerShipType === OwnershipType.DIRECT),
  );

  useEffect(() => {
    if (assets?.length === 0) {
      modals.closeModal();
      modals.openModal({
        type: 'IWCreateAssetModal',
      });
    }
  }, [assets]);

  const assetsNoOwnership = useMemo(() => {
    const selectedAssets = assets?.filter((item) => selectedAssetIds.includes(item.id));
    return selectedAssets?.filter((item) => !item.ownerShipType);
  }, [selectedAssetIds]);

  const onFinishHandler = async (values: SelectAssetsModalParams) => {
    if (tab === 0 && assetsNoOwnership?.length) {
      setTab(1);
      return;
    }
    if (tab === 1 && assetsNoOwnership?.length) {
      const newAssets = assetsNoOwnership.map((item) => {
        return {
          ...item,
          ownerShipType: values.assetOwnerships[item.id],
        };
      });
      await sjDatabase.collections.assets?.updateMany(newAssets);
    }
    const selectedDirectAssets = sjDatabase.collections.assets?.findMany(
      (asset) =>
        values.selectedAssets.includes(asset.id) && asset.ownerShipType === OwnershipType.DIRECT,
    );
    updateAssets([...savedAssets, ...(selectedDirectAssets || [])]);
    modals.closeModal();
  };

  useEffect(() => {
    if (formState.errors.assetOwnerships) {
      const field = Object.values(formState.errors.assetOwnerships)[0];
      if (field?.ref?.name) {
        // antd does not set proper refs
        document.querySelector(`[name="${field?.ref?.name}"]`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
    }
  }, [formState]);

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm scrollToFirstError onFinish={formHandleSubmit(onFinishHandler)}>
        {tab === 0 && (
          <>
            <CheckboxWrapper>
              <Bold>{t('description')}</Bold>
              <Controller
                name="selectedAssets"
                control={control}
                render={({ field }) => (
                  <Checkbox.Group
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                      margin: '24px 0 10px',
                    }}
                    options={assets?.map((item) => ({
                      label: item.nickName || item.nickname,
                      value: item.id,
                    }))}
                    onChange={field.onChange}
                  />
                )}
              />
              <ButtonIwText
                onClick={() => {
                  modals.closeModal();
                  modals.openModal({
                    type: 'IWCreateAssetModal',
                  });
                }}
                style={{ width: 'fit-content' }}
                icon={<Icon name="plus" size="s20" />}
              >
                {t('addNewBtn')}
              </ButtonIwText>
            </CheckboxWrapper>
          </>
        )}
        {tab === 1 && (
          <AssetsWrapper>
            <Bold>{t('specifyOwnersip')}</Bold>
            {assetsNoOwnership?.map((item) => (
              <AssetWrapper key={item.id}>
                <Bold>{item.nickname || item.nickName}</Bold>

                <Controller
                  name={`assetOwnerships.${item.id}`}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <FieldContainerIw label={t('ownershipType')} required>
                      <Form.Item validateStatus={fieldState.error ? 'error' : ''}>
                        <SelectAssetOwnershipType {...field} />
                      </Form.Item>
                    </FieldContainerIw>
                  )}
                />
                {assetOwnerships[item.id] === OwnershipType.INDIRECT && (
                  <Alert text={t('directWarning')} color={'yellow'} />
                )}
              </AssetWrapper>
            ))}
          </AssetsWrapper>
        )}
        <Buttons>
          <ButtonIw
            htmlType="submit"
            loading={formState.isSubmitting}
            disabled={formState.isSubmitting}
          >
            {t('btn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const Bold = styled.span`
  font-weight: 700;
  font-size: 16px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;

const AssetWrapper = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AssetsWrapper = styled.div`
  max-height: 600px;
  overflow: auto;
  padding: 0 3px 2px;
`;

const CheckboxWrapper = styled.div`
  max-height: 600px;
  overflow: auto;

  ${media.mobile} {
    max-height: unset;
  }
`;
